// AuxiliarCuentaXL - Sprint 44 Fase 3 Vista 8.
// Reporte auxiliar por tercero (cliente/proveedor).
import { useEffect, useMemo, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../../design/motion';
import * as svc from '../services/contabilidadService';
import XLInput from '../../../components/xl/XLInput';
import XLSelect from '../../../components/xl/XLSelect';
import XLTable from '../../../components/xl/XLTable';
import { fmt } from '../utils';

const CSS_ID = 'xl-rep-aux-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-page__sub { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 16px }

.xl-tools {
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); padding: 16px; margin-bottom: 16px;
  box-shadow: var(--xl-shadow-sm);
  display: grid; grid-template-columns: 1fr 1fr 1fr 1fr auto; gap: 12px;
  align-items: end;
}
@media (max-width: 1000px) { .xl-tools { grid-template-columns: 1fr } }
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
  background: var(--xl-color-primary); color: var(--xl-color-text-inverse);
}
.xl-btn:hover { background: var(--xl-color-primary-hover) }
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }

.xl-banner-err {
  background: rgba(220,38,38,0.10); color: var(--xl-color-danger);
  border: 1px solid rgba(220,38,38,0.28);
  padding: 10px 14px; margin-bottom: 16px;
  border-radius: var(--xl-radius-md); font-size: 13px;
}

.xl-aux-totals {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  box-shadow: var(--xl-shadow-sm);
  padding: 14px 16px;
  margin-bottom: 12px;
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px;
  font-size: 13px;
}
@media (max-width: 720px) { .xl-aux-totals { grid-template-columns: 1fr } }
.xl-aux-totals__lbl { color: var(--xl-color-text-secondary) }
.xl-aux-totals__val { font-family: var(--xl-font-mono); font-weight: 600; color: var(--xl-color-text); margin-left: 6px }
.xl-aux-totals__val--pos { color: var(--xl-color-success) }
.xl-aux-totals__val--neg { color: var(--xl-color-danger) }
.xl-mono { font-family: var(--xl-font-mono); font-size: 12px }
`;

const TERCERO_OPTIONS = [
  { value: 'cliente',   label: 'Cliente' },
  { value: 'proveedor', label: 'Proveedor' },
];

export default function AuxiliarCuentaXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const firstDay = today.slice(0, 7) + '-01';
  const [tipo, setTipo] = useState('cliente');
  const [terceroId, setTerceroId] = useState('');
  const [desde, setDesde] = useState(firstDay);
  const [hasta, setHasta] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      setData(await svc.auxiliarTercero({
        tercero_tipo: tipo, tercero_id: terceroId, desde, hasta,
      }));
    } catch (e) {
      setErr((e && e.message) || 'Error'); setData(null);
    } finally { setLoading(false); }
  };

  const columns = useMemo(() => ([
    { key: 'fecha', label: 'Fecha' },
    { key: 'cuenta', label: 'Cuenta',
      render: (m) => <span className="xl-mono">{m.cuenta && m.cuenta.codigo}</span> },
    { key: 'referencia', label: 'Ref',
      render: (m) => <span className="xl-mono">{m.referencia || '\u2014'}</span> },
    { key: 'descripcion', label: 'Descripcion',
      render: (m) => m.descripcion || '\u2014' },
    { key: 'debito', label: 'Debito', align: 'right',
      render: (m) => <span className="xl-mono">{fmt(m.debito)}</span> },
    { key: 'credito', label: 'Credito', align: 'right',
      render: (m) => <span className="xl-mono">{fmt(m.credito)}</span> },
  ]), []);

  const saldoCls = data && Number(data.saldo) >= 0
    ? 'xl-aux-totals__val xl-aux-totals__val--pos'
    : 'xl-aux-totals__val xl-aux-totals__val--neg';

  return (
    <div className="xl-page">
      <h2 className="xl-page__title">Auxiliar por tercero</h2>
      <p className="xl-page__sub">Movimientos por cliente o proveedor.</p>

      <section className="xl-tools" aria-label="Filtros auxiliar">
        <XLSelect label="Tipo" value={tipo}
          onChange={(e) => setTipo(e.target.value)} options={TERCERO_OPTIONS} />
        <XLInput label="ID tercero (opcional)"
          value={terceroId}
          onChange={(e) => setTerceroId(e.target.value)}
          placeholder="ej: 5" />
        <XLInput label="Desde" type="date" value={desde}
          onChange={(e) => setDesde(e.target.value)} />
        <XLInput label="Hasta" type="date" value={hasta}
          onChange={(e) => setHasta(e.target.value)} />
        <button type="button" className="xl-btn"
          onClick={load} disabled={loading}>
          {loading ? '\u2026' : 'Generar'}
        </button>
      </section>

      {err && <div className="xl-banner-err" role="alert">{err}</div>}

      {data && (
        <>
          <div className="xl-aux-totals">
            <div>
              <span className="xl-aux-totals__lbl">Debito total:</span>
              <span className="xl-aux-totals__val">{fmt(data.total_debito)}</span>
            </div>
            <div>
              <span className="xl-aux-totals__lbl">Credito total:</span>
              <span className="xl-aux-totals__val">{fmt(data.total_credito)}</span>
            </div>
            <div>
              <span className="xl-aux-totals__lbl">Saldo:</span>
              <span className={saldoCls}>{fmt(data.saldo)}</span>
            </div>
          </div>
          <XLTable
            ariaLabel="Auxiliar por tercero"
            columns={columns}
            data={data.items || []}
            loading={loading}
            rowKey="id"
            emptyMessage="Sin movimientos." />
        </>
      )}
    </div>
  );
}
